import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';

const ResourceVoicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resource, setResource] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState(null);
  
  // Voice state
  const [existingAudio, setExistingAudio] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [audioDuration, setAudioDuration] = useState(null);
  
  const audioRef = useRef(null);
  
  // Check if user is admin
  const checkAdminStatus = useCallback(async () => {
    if (user) {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('subscription_status')
        .eq('user_id', user.id)
        .single();
        
      if (!error && data) {
        const admin = data.subscription_status === 'admin';
        setIsAdmin(admin);
        
        if (!admin) {
          // Redirect non-admin users
          navigate('/resources');
        }
      } else {
        navigate('/resources');
      }
    } else {
      navigate('/signin', { state: { from: `/resources/voice/${id}` } });
    }
  }, [user, navigate, id]);
  
  // Fetch resource data
  const fetchResource = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('resources')
        .select(`
          *,
          resource_voices (id, audio_url, duration)
        `)
        .eq('id', id)
        .single();
        
      if (error) throw error;
      
      if (!data) {
        throw new Error('Resource not found');
      }
      
      setResource(data);
      
      // Check if there's an existing voice recording
      if (data.resource_voices && data.resource_voices.length > 0) {
        const voice = data.resource_voices[0];
        setExistingAudio(voice);
        setAudioUrl(voice.audio_url);
        setAudioDuration(voice.duration);
      }
    } catch (error) {
      console.error('Error fetching resource:', error);
      setError('Failed to load resource data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [id]);
  
  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);
  
  useEffect(() => {
    if (isAdmin) {
      fetchResource();
    }
  }, [isAdmin, fetchResource]);
  
  // Handle audio file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('audio/')) {
      setError('Please select an audio file');
      return;
    }
    
    setAudioFile(file);
    setAudioUrl(URL.createObjectURL(file));
    
    // Reset audio duration when a new file is selected
    setAudioDuration(null);
  };
  
  // Handle audio playback
  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  // Handle metadata loaded (to get duration)
  const handleMetadataLoaded = () => {
    if (audioRef.current) {
      setAudioDuration(Math.round(audioRef.current.duration));
    }
  };
  
  // Format time in seconds to mm:ss
  const formatTime = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Upload audio file to storage
  const uploadAudio = async () => {
    if (!audioFile) return null;
    
    setIsUploading(true);
    try {
      const fileExt = audioFile.name.split('.').pop();
      const filePath = `resource-audio/${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('public')
        .upload(filePath, audioFile, {
          cacheControl: '3600',
          upsert: false,
          onUploadProgress: (progress) => {
            setUploadProgress(Math.round((progress.loaded / progress.total) * 100));
          }
        });
        
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('public')
        .getPublicUrl(filePath);
        
      return publicUrlData.publicURL;
    } catch (error) {
      console.error('Error uploading audio:', error);
      throw error;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!audioFile && !existingAudio) {
      setError('Please select an audio file');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      // Upload audio if there's a new one
      let finalAudioUrl = audioUrl;
      if (audioFile) {
        finalAudioUrl = await uploadAudio();
      }
      
      const voiceData = {
        resource_id: id,
        audio_url: finalAudioUrl,
        duration: audioDuration
      };
      
      // Check if we're updating or creating
      if (existingAudio) {
        // Update existing voice record
        const { error } = await supabase
          .from('resource_voices')
          .update(voiceData)
          .eq('id', existingAudio.id);
          
        if (error) throw error;
      } else {
        // Create new voice record
        const { error } = await supabase
          .from('resource_voices')
          .insert([voiceData]);
          
        if (error) throw error;
      }
      
      // Navigate back
      navigate(`/resources/${resource.slug}`);
    } catch (error) {
      console.error('Error saving voice:', error);
      setError('Failed to save audio. Please try again later.');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return <LoadingScreen message="Loading resource..." />;
  }
  
  if (!isAdmin) {
    return <LoadingScreen message="Checking permissions..." />;
  }
  
  if (!resource) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Error</h1>
        <p className="text-red-600 mb-6">Resource not found</p>
        <Link
          to="/resources/manage"
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
        >
          Back to Resources
        </Link>
      </div>
    );
  }
  
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {existingAudio ? 'Update Voice Recording' : 'Add Voice Recording'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {`For resource: "${resource.title}"`}
        </p>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="space-y-6">
            {/* Resource Summary */}
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h2 className="font-medium text-gray-900 mb-2">Resource Summary</h2>
              <p className="text-gray-700">{resource.summary}</p>
            </div>
            
            {/* Audio File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Audio File <span className="text-red-500">*</span>
              </label>
              
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                    aria-hidden="true"
                  >
                    <path
                      d="M24 30.75c6.213 0 11.25-5.037 11.25-11.25S30.213 8.25 24 8.25 12.75 13.287 12.75 19.5 17.787 30.75 24 30.75z"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14.25 20.25h19.5M14.25 18.75h19.5M14.25 21.75h19.5"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="flex justify-center text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                    >
                      <span>{audioFile || existingAudio ? 'Replace audio file' : 'Upload audio file'}</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        className="sr-only"
                        accept="audio/*"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">MP3, WAV, M4A up to 50MB</p>
                </div>
              </div>
              
              {isUploading && (
                <div className="mt-4">
                  <div className="h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-primary-600 rounded-full"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="mt-1 text-xs text-center text-gray-500">
                    Uploading: {uploadProgress}%
                  </p>
                </div>
              )}
            </div>
            
            {/* Audio Preview */}
            {audioUrl && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Audio Preview</h3>
                <div className="bg-gray-50 p-4 rounded-md">
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={handlePlayPause}
                      className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      {isPlaying ? (
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <div className="ml-4 flex-1">
                      <p className="text-sm text-gray-700">
                        {audioFile ? audioFile.name : 'Current audio'}
                      </p>
                      <p className="text-xs text-gray-500">
                        Duration: {formatTime(audioDuration)}
                      </p>
                    </div>
                  </div>
                  
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    className="hidden"
                    onEnded={() => setIsPlaying(false)}
                    onLoadedMetadata={handleMetadataLoaded}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Form actions */}
        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
          <Link
            to={`/resources/${resource.slug}`}
            className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving || isUploading || (!audioFile && !existingAudio)}
            className={`px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
              (saving || isUploading || (!audioFile && !existingAudio)) ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            {saving ? 'Saving...' : existingAudio ? 'Update Audio' : 'Add Audio'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceVoicePage; 